import React from "react";
import PropTypes from "prop-types";
import Page from "../../../components/BaseComponents/Page/Page";
import {FaSolidIcon} from "../../../components/FontAwesomeIcons";
import {PageFooter} from "../../../components/BaseComponents/Page/PageFooter";
import {CreateEventTypeStep} from "./CreateEventTypeStep";
import {CreateEventDetailsStep} from "./CreateEventDetailsStep";
import {CreateEventParticipantsStep} from "./CreateEventParticipantsStep";
import {CreateEventOptionsStep} from "./CreateEventOptionsStep";
import {
    USER_EVENT_THEME_COLORS,
    USER_EVENT_THEME_FONTS,
    USER_EVENT_TYPE_PHOTO_ALBUM,
    USER_EVENT_TYPE_RESERVATIONS
} from "./CreateEventConstants";
import {CreateEventStepProgress} from "./CreateEventStepProgress";
import {swal, swalError, swalLoading, swalSuccess} from "../../../helpers/SweetAlertWrapper";
import {CreateEventLandingStyleStep} from "./CreateEventLandingStyleStep";
import AuthAPI from "../../../api/AuthAPI";
import {UsersModuleEventsAPI} from "../../../api/UsersModuleEvents/UsersModuleEventsAPI";
import {UsersModulePhotoAlbumsAPI} from "../../../api/UsersModulePhotoAlbums/UsersModulePhotoAlbumsAPI";

export class CreateEventPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            currentStep: "type",
            isSaving: false,
            eventData: {
                type: null,
                cover: null,
                name: "",
                startDate: "",
                endDate: "",
                placeName: "",
                address: "",
                latitude: "",
                longitude: "",
                directions: "",
                description: "",
                participantsNameMode: "all",
                expectedParticipantsEnabled: false,
                expectedParticipants: "",
                maxParticipantsPerReservationEnabled: false,
                maxParticipantsPerReservation: "",
                showIntolerances: false,
                showNotes: false,
                guestsApprovalMode: "confirmed",
                externalLinksEnabled: false,
                externalLinks: [],
                photoAlbumEnabled: false,
                themeColor: USER_EVENT_THEME_COLORS[0].value,
                themeFont: Object.keys(USER_EVENT_THEME_FONTS)[0],
            }
        };
        this.progressContainerStyle = {
            height: "6px",
        };
    }

    handleTypeChoose = type => {
        this.setState({
            eventData: {
                ...this.state.eventData,
                type: type,
            }
        }, this.handleNextStep);
    }

    handleDataChange = field => value => {
        this.setState({
            eventData: {
                ...this.state.eventData,
                [field]: value,
            }
        });
    }

    handleCheckboxChange = field => e => {
        this.handleDataChange(field)(e.target.checked);
    }

    handleInputChange = field => e => {
        this.handleDataChange(field)(e.target.value);
    }

    handleLinksChange = links => {
        this.setState({
            eventData: {
                ...this.state.eventData,
                externalLinks: links,
            }
        });
    }

    handleNextStep = () => {
        if (this.state.currentStep === "type") {
            this.setState({currentStep: "details"});
            return;
        }
        if (this.state.currentStep === "details" && this.state.eventData.type === USER_EVENT_TYPE_RESERVATIONS) {
            if (!this.isDetailsStepValid()) {
                this.showMissingFieldsAlert();
                return;
            }
            this.setState({currentStep: "participants"});
            return;
        }
        if (this.state.currentStep === "details") {
            if (!this.isDetailsStepValid()) {
                this.showMissingFieldsAlert();
                return;
            }
            this.setState({currentStep: "landingStyle"});
            return;
        }
        if (this.state.currentStep === "participants") {
            if (!this.isParticipantsStepValid()) {
                this.showParticipantsValidationAlert();
                return;
            }
            this.setState({currentStep: "options"});
            return;
        }
        if (this.state.currentStep === "options") {
            this.setState({currentStep: "landingStyle"});
        }
    }

    handlePreviousStep = _ => {
        if (this.state.currentStep === "details") {
            this.setState({currentStep: "type"});
            return;
        }
        if (this.state.currentStep === "participants") {
            this.setState({currentStep: "details"});
            return;
        }
        if (this.state.currentStep === "options") {
            this.setState({currentStep: "participants"});
            return;
        }
        if (this.state.currentStep === "landingStyle") {
            this.setState({currentStep: this.state.eventData.type === USER_EVENT_TYPE_RESERVATIONS ? "options" : "details"});
        }
    }

    getTotalSteps = _ => this.state.eventData.type === USER_EVENT_TYPE_RESERVATIONS ? 4 : 2

    getCurrentStepNumber = _ => {
        switch (this.state.currentStep) {
            case "details":
                return 1;
            case "participants":
                return 2;
            case "options":
                return 3;
            case "landingStyle":
                return this.state.eventData.type === USER_EVENT_TYPE_RESERVATIONS ? 4 : 2;
            default:
                return 0;
        }
    }

    canShowStepProgress = _ => this.state.currentStep !== "type"

    getTodayDateValue = _ => {
        const now = new Date();
        const month = `${now.getMonth() + 1}`.padStart(2, "0");
        const day = `${now.getDate()}`.padStart(2, "0");
        return `${now.getFullYear()}-${month}-${day}`;
    }

    getDetailsMissingFields = _ => {
        const missingFields = [];
        if (!this.state.eventData.name.trim()) {
            missingFields.push("Nome evento");
        }
        if (!this.state.eventData.startDate) {
            missingFields.push("Inizio evento");
        }
        if (!this.state.eventData.endDate) {
            missingFields.push("Fine evento");
        }
        if (this.state.eventData.startDate &&
            this.state.eventData.startDate.substring(0, 10) < this.getTodayDateValue()) {
            missingFields.push("Data evento non precedente a oggi");
        }
        if (this.state.eventData.startDate &&
            this.state.eventData.endDate &&
            this.state.eventData.endDate <= this.state.eventData.startDate) {
            missingFields.push("Fine evento successiva all'inizio");
        }
        return missingFields;
    }

    isDetailsStepValid = _ => !this.getDetailsMissingFields().length

    getParticipantsValidationErrors = _ => {
        const validationErrors = [];
        const expectedParticipants = parseInt(this.state.eventData.expectedParticipants, 10);
        const maxParticipantsPerReservation = parseInt(this.state.eventData.maxParticipantsPerReservation, 10);

        if (this.state.eventData.expectedParticipantsEnabled &&
            this.state.eventData.maxParticipantsPerReservationEnabled &&
            !Number.isNaN(expectedParticipants) &&
            !Number.isNaN(maxParticipantsPerReservation) &&
            maxParticipantsPerReservation > expectedParticipants) {
            validationErrors.push("I partecipanti per conferma non possono superare i partecipanti totali");
        }
        return validationErrors;
    }

    isParticipantsStepValid = _ => !this.getParticipantsValidationErrors().length

    hasMaxParticipantsPerReservationError = _ => this.getParticipantsValidationErrors()
        .includes("I partecipanti per conferma non possono superare i partecipanti totali")

    canShowNextButton = _ => this.state.currentStep === "details" ||
        (this.state.eventData.type === USER_EVENT_TYPE_RESERVATIONS &&
            (this.state.currentStep === "participants" || this.state.currentStep === "options"))

    canShowSaveButton = _ => this.state.currentStep === "landingStyle"

    formatSubmitDate = date => date ? date.replace("T", " ") : ""

    parseOptionalFloat = value => value !== "" && value !== null && value !== undefined ? parseFloat(value) : null

    parseOptionalInt = value => value !== "" && value !== null && value !== undefined ? parseInt(value, 10) : null

    getReservationDefaultStatus = _ => this.state.eventData.guestsApprovalMode === "confirmed" ? "confirmed" : "pending"

    getSaveAPI = _ => this.state.eventData.type === USER_EVENT_TYPE_PHOTO_ALBUM ? UsersModulePhotoAlbumsAPI : UsersModuleEventsAPI

    handleSaveAndShare = async _ => {
        if (this.state.eventData.type === USER_EVENT_TYPE_RESERVATIONS && !this.isParticipantsStepValid()) {
            this.showParticipantsValidationAlert();
            return;
        }

        const selectedFont = USER_EVENT_THEME_FONTS[this.state.eventData.themeFont] || USER_EVENT_THEME_FONTS.roboto;

        this.setState({isSaving: true});
        swalLoading("Salvataggio evento in corso...");

        const formData = new FormData();
        const latitude = this.parseOptionalFloat(this.state.eventData.latitude);
        const longitude = this.parseOptionalFloat(this.state.eventData.longitude);
        formData.append("token", AuthAPI.getUserAccessToken());
        formData.append("event_type", this.state.eventData.type);
        formData.append("name", this.state.eventData.name);
        formData.append("description", this.state.eventData.description);
        formData.append("start_date", this.formatSubmitDate(this.state.eventData.startDate));
        formData.append("end_date", this.formatSubmitDate(this.state.eventData.endDate));
        formData.append("location[name]", this.state.eventData.placeName);
        formData.append("location[address]", this.state.eventData.address);
        formData.append("location[latitude]", latitude !== null ? latitude : "");
        formData.append("location[longitude]", longitude !== null ? longitude : "");
        formData.append("location[directions]", this.state.eventData.directions);
        formData.append("settings[landing_font_family]", selectedFont.label);
        formData.append("settings[landing_primary_color]", this.state.eventData.themeColor);
        formData.append("settings[landing_secondary_color]", "#ffffff");
        formData.append("settings[landing_background_color]", "#ffffff");

        if (this.state.eventData.type === USER_EVENT_TYPE_RESERVATIONS && this.state.eventData.eventId) {
            formData.append("event_id", this.state.eventData.eventId);
        }
        if (this.state.eventData.type === USER_EVENT_TYPE_PHOTO_ALBUM && this.state.eventData.eventAlbumId) {
            formData.append("event_album_id", this.state.eventData.eventAlbumId);
        }
        if (this.state.eventData.type === USER_EVENT_TYPE_RESERVATIONS) {
            formData.append("expected_participants", this.state.eventData.expectedParticipantsEnabled ?
                this.parseOptionalInt(this.state.eventData.expectedParticipants) || 0 : 0);
            formData.append("max_participants_per_reservation", this.state.eventData.maxParticipantsPerReservationEnabled ?
                this.parseOptionalInt(this.state.eventData.maxParticipantsPerReservation) || 0 : 0);
            formData.append("settings[collect_all_participants_names]", this.state.eventData.participantsNameMode === "all" ? 1 : 0);
            formData.append("settings[show_intolerances]", this.state.eventData.showIntolerances ? 1 : 0);
            formData.append("settings[show_notes]", this.state.eventData.showNotes ? 1 : 0);
            formData.append("settings[reservation_default_status]", this.getReservationDefaultStatus());
            this.state.eventData.externalLinks.forEach((link, index) => {
                formData.append(`links[${index}][name]`, link.label);
                formData.append(`links[${index}][url]`, link.url);
            });
        }

        if (this.state.eventData.cover) {
            let coverObjectUrl = null;
            try {
                coverObjectUrl = URL.createObjectURL(this.state.eventData.cover);
                const coverBlob = await fetch(coverObjectUrl).then(response => response.blob());
                formData.append("image", coverBlob);
                URL.revokeObjectURL(coverObjectUrl);
            } catch (error) {
                if (coverObjectUrl) {
                    URL.revokeObjectURL(coverObjectUrl);
                }
                swal.safeClose();
                swalError("Non è stato possibile leggere l'immagine caricata.");
                this.setState({isSaving: false});
                return;
            }
        }

        this.getSaveAPI().User.save(formData)
            .then(response => {
                swal.safeClose();
                swalSuccess(response.message || "Evento salvato correttamente.").then(_ => {
                    this.setState({isSaving: false});
                    this.props.onDismiss();
                })
            })
            .catch(error => {
                swal.safeClose();
                swalError(error);
                this.setState({isSaving: false});
            })
    }

    showMissingFieldsAlert = _ => {
        const missingFields = this.getDetailsMissingFields();
        if (!missingFields.length) {
            return;
        }
        swalError(
            <div className={"text-left"}>
                <div className={"font-weight-bold mb-2"}>Per proseguire compila:</div>
                <ul className={"mb-0 pl-3"}>
                    {missingFields.map(field => <li key={field}>{field}</li>)}
                </ul>
            </div>
        );
    }

    showParticipantsValidationAlert = _ => {
        const validationErrors = this.getParticipantsValidationErrors();
        if (!validationErrors.length) {
            return;
        }
        swalError(
            <div className={"text-left"}>
                <div className={"font-weight-bold mb-2"}>Controlla i partecipanti:</div>
                <ul className={"mb-0 pl-3"}>
                    {validationErrors.map(error => <li key={error}>{error}</li>)}
                </ul>
            </div>
        );
    }

    render() {
        return (
            <Page
                title={"Crea evento"}
                leftIcon={<FaSolidIcon name={"chevron-left"}/>}
                onLeftIconClick={this.props.onDismiss}
                active={true}
            >
                {
                    this.canShowStepProgress() &&
                    <CreateEventStepProgress
                        currentStep={this.getCurrentStepNumber()}
                        totalSteps={this.getTotalSteps()}
                        onBack={this.handlePreviousStep}
                        progressContainerStyle={this.progressContainerStyle}
                    />
                }

                <div>
                    {
                        this.state.currentStep === "type" &&
                        <CreateEventTypeStep
                            onChoose={this.handleTypeChoose}
                        />
                    }

                    {
                        this.state.currentStep === "details" &&
                        <CreateEventDetailsStep
                            eventData={this.state.eventData}
                            onInputChange={this.handleInputChange}
                            onDataChange={this.handleDataChange}
                            minEventDate={this.getTodayDateValue()}
                        />
                    }

                    {
                        this.state.currentStep === "participants" &&
                        <CreateEventParticipantsStep
                            eventData={this.state.eventData}
                            onCheckboxChange={this.handleCheckboxChange}
                            onInputChange={this.handleInputChange}
                            onDataChange={this.handleDataChange}
                            hasMaxParticipantsPerReservationError={this.hasMaxParticipantsPerReservationError()}
                        />
                    }

                    {
                        this.state.currentStep === "options" &&
                        <CreateEventOptionsStep
                            eventData={this.state.eventData}
                            onCheckboxChange={this.handleCheckboxChange}
                            onLinksChange={this.handleLinksChange}
                        />
                    }

                    {
                        this.state.currentStep === "landingStyle" &&
                        <CreateEventLandingStyleStep
                            eventData={this.state.eventData}
                            onDataChange={this.handleDataChange}
                        />
                    }
                </div>

                {
                    this.canShowNextButton() &&
                    <PageFooter className={"bg-white border-top shadow p-3"}>
                        <button
                            type={"button"}
                            className={"btn btn-primary btn-lg btn-block"}
                            onClick={this.handleNextStep}
                        >
                            Avanti <FaSolidIcon name={"chevron-right"} className={"ml-1"}/>
                        </button>
                    </PageFooter>
                }

                {
                    this.canShowSaveButton() &&
                    <PageFooter className={"bg-white border-top shadow p-3"}>
                        <button
                            type={"button"}
                            className={"btn btn-primary btn-lg btn-block"}
                            onClick={this.handleSaveAndShare}
                            disabled={this.state.isSaving}
                        >
                            Salva e condividi <FaSolidIcon name={"share-alt"} className={"ml-1"}/>
                        </button>
                    </PageFooter>
                }
            </Page>
        );
    }
}

CreateEventPage.propTypes = {
    onDismiss: PropTypes.func,
};
